/**
 * Where a harvested node comes back.
 *
 * Chopping a tree used to leave a hole that refilled itself: the regrow timer ran
 * out and the SAME tree reappeared on the SAME tile, so a cleared patch was never
 * really cleared and the world never changed shape. A forest that heals in place
 * is scenery, not a resource.
 *
 * So a node that regrows moves. When its timer expires it is re-seeded on a free
 * tile a short walk away — near enough that the area keeps its character (a birch
 * grove stays a birch grove), far enough that the ground you cleared stays clear
 * and the map slowly drifts as it is worked.
 *
 * The world is generated on the client from a seed and the server does not know
 * the map, so this is necessarily client-side. That is fine: the server's
 * HarvestState is keyed by TILE, and a relocated node simply leaves its old tile's
 * record to expire — no stale state, no migration.
 *
 * ─── Which cleared tiles are re-seeded: none ─────────────────────────────────
 *
 * The owner asked for a ruling, and the ruling is that a cell the player cleared
 * is retired, not merely delayed. Two mechanisms, one rule:
 *
 *   OBSTACLES (tree, ore vein, boulder/stone) do not regrow at all. The server
 *   stamps their tile with a never-expiring HarvestState row, so the clear
 *   SURVIVES A RELOAD for free — FieldService prunes expired rows and returns the
 *   rest, so a row that never expires is replayed on every future load and the
 *   node stays hidden. Relocation was rejected as the sole answer for these
 *   because it cannot survive a reload: the map is re-derived from the seed on
 *   every load, so a relocated tree would be back at its original tile tomorrow —
 *   which is exactly the bug, just slower.
 *
 *   RENEWABLES (mushrooms, bushes, grass, flowers) still respawn on their timer,
 *   and THAT is what this file relocates: when the timer elapses, the node is
 *   re-seeded on free, unreserved ground nearby rather than on the tile it was
 *   taken from. They block nothing, so they never made the world unusable; the
 *   relocation is what keeps a worked patch looking worked.
 */

import { TILE, canHostNode, type Decor } from './world';

/** How far (in tiles) a regrown node may travel from where it was taken. */
export const RELOCATE_MIN_TILES = 2;
export const RELOCATE_MAX_TILES = 7;
/** Tries before giving up and letting it regrow where it stood. */
const ATTEMPTS = 20;

export interface RelocateWorld {
  /** True when a tile cannot host a node (water, cliff, building, plot, occupied). */
  blocked(tx: number, ty: number): boolean;
  /** Move the decor's index entry from its old bucket to the one for the new tile. */
  reindex(d: Decor, fromTx: number, fromTy: number): void;
  /**
   * Ground the player has taken — cleared for good, or built on (see world.ts
   * `reservedTiles`). Checked separately from `blocked` because these tiles are
   * mostly EMPTY: a road is walkable and a cleared patch is bare grass, so an
   * occupancy test that looks for something standing there says "free" about the
   * two cases the owner actually reported.
   */
  reserved?: ReadonlySet<string>;
}

/**
 * Pick a new home for `d` and move it there. Returns true when it moved.
 *
 * Deliberately keeps the node's identity (`d.id`) so every timer map keyed by id
 * — felledUntil, minedUntil, gatheredUntil, boulderHits — stays coherent across
 * the move; only its coordinates change.
 */
export function relocateRegrown(d: Decor, w: RelocateWorld, rand: () => number = Math.random): boolean {
  const fromTx = d.tx, fromTy = d.ty;
  // "Give up and stay put" is only acceptable on ground the player has NOT claimed.
  // On a cleared or built-on tile, staying put IS the reported bug, so the search
  // widens instead of falling back — the node walks further out rather than
  // sprouting through the road that was laid over it.
  const mustLeave = !canHostNode(w.reserved, fromTx, fromTy);
  const passes = mustLeave ? [1, 2, 4] : [1];
  for (const spread of passes) {
    for (let i = 0; i < ATTEMPTS; i++) {
      // Uniform over the annulus by angle + radius. The exact distribution does not
      // matter; not landing back on top of the old tile does, hence the MIN radius.
      const ang = rand() * Math.PI * 2;
      const rad = (RELOCATE_MIN_TILES + rand() * (RELOCATE_MAX_TILES - RELOCATE_MIN_TILES)) * spread;
      const tx = Math.round(fromTx + Math.cos(ang) * rad);
      const ty = Math.round(fromTy + Math.sin(ang) * rad);
      if (tx === fromTx && ty === fromTy) continue;
      if (w.blocked(tx, ty)) continue;
      if (!canHostNode(w.reserved, tx, ty)) continue;
      d.tx = tx; d.ty = ty;
      w.reindex(d, fromTx, fromTy);
      return true;
    }
  }
  return false;   // nowhere to go (dense forest, walled in) — regrow in place
}

/** World pixel centre of a decor's tile, for repositioning its sprite. */
export const decorCenter = (d: Decor) => ({ x: (d.tx + 0.5) * TILE, y: (d.ty + 1) * TILE });
